// Stage 201-S + 202-W/J: witness-reframe + joint-reframe subscriber.
//
// Watches the Stage-97 useGadflyTurn seam. On the settled turn (turnEnded
// true, one fire per (turnId, userMessageId)):
//
//   1. POST the latest user message to /api/laughter/witness-detect with
//      the conversationId + turnId. The endpoint runs the lexical
//      pre-filter and, when laughter_semantic_witness_enabled is on
//      (Stage 202-W), an LLM judge over the conversation window. The
//      returned `detector` / `semantic` fields are threaded into the
//      engram so it records HOW it was detected.
//   2. If matched, deposit a witness-reframe engram.
//   3. Joint-reframe:
//      - shared-ground OFF (Stage 201-S): if a witness-reframe matched
//        AND a self-laugh fired this turn, deposit a same-turn joint.
//      - shared-ground ON (Stage 202-J): if there is any reframe signal
//        this turn (witness matched OR self-laugh fired), call the
//        two-pass /api/laughter/shared-ground-detect; if it confirms
//        (shifted && bothParties && conf != low), deposit a shared-ground
//        joint. The cheap same-turn join is NOT also written.
//
// The self-laugh set is derived directly from the settled bites/swats via
// finalTagOf (decoupled from the Stage-201-X pulse store, race-free).
//
// One-shot per (turnId, userMessageId): a ref short-circuits re-renders.
// The worker's content-hash idempotency is the second line of defence.

import { useEffect, useRef } from 'react';
import { useGadflyTurn } from '../../hooks/useGadflyTurn';
import {
  laughterWitnessDetect,
  laughterSharedGroundDetect,
  laughterDeposit,
} from '../../api/laughter';
import { finalTagOf } from './finalTag';
import { buildWitnessReframeEngram } from './buildWitnessReframeEngram';
import { buildJointReframeEngram } from './buildJointReframeEngram';
import type { ChatMessage } from '../../hooks/useChat';

interface UseWitnessReframeSubscriberArgs {
  enabled: boolean;
  conversationId: string | null;
  latestUserMsg: ChatMessage | null;
  /** Stage 202-J: gates the two-pass shared-ground deep joint path. */
  sharedGroundEnabled: boolean;
}

export function useWitnessReframeSubscriber(
  args: UseWitnessReframeSubscriberArgs,
): void {
  const { enabled, conversationId, latestUserMsg, sharedGroundEnabled } = args;
  const turn = useGadflyTurn();
  const processedRef = useRef<string | null>(null); // `${turnId}::${msgId}`

  useEffect(() => {
    if (!enabled) return;
    if (!conversationId) return;
    if (!turn.turnId) return;
    if (!turn.turnEnded) return;
    if (!latestUserMsg || !latestUserMsg.text) return;
    const key = `${turn.turnId}::${latestUserMsg.id}`;
    if (processedRef.current === key) return;
    // Mark BEFORE the async work — prevents Strict-Mode double-fire.
    processedRef.current = key;

    const turnId = turn.turnId;
    const userText = latestUserMsg.text;
    // Self-laugh set: indices of bites whose settled tag is ACCEPT. Same
    // determination useLaughterSubscriber uses to deposit self-reframes.
    const selfFiredBiteIndices = (turn.bites ?? [])
      .filter((b) => finalTagOf(b, turn.swatEntries) === 'ACCEPT')
      .map((b) => b.index);

    void (async () => {
      try {
        const detect = await laughterWitnessDetect({
          text: userText,
          conversationId,
          turnId,
        });
        const firedAt = new Date().toISOString();

        // 1. Witness engram — only when the detector matched.
        if (detect.matched) {
          await laughterDeposit(
            buildWitnessReframeEngram({
              conversationId,
              turnId,
              userMessage: userText,
              normalisedText: detect.normalisedText,
              cues: detect.cues,
              firedAt,
              detector: detect.detector,
              semantic: detect.semantic,
            }),
          );
        }

        // 2. Joint path.
        if (sharedGroundEnabled) {
          // Deep path: run the two-pass read only when a reframe signal
          // already exists this turn (cost-gated).
          if (detect.matched || selfFiredBiteIndices.length > 0) {
            const sg = await laughterSharedGroundDetect({ conversationId });
            if (
              sg.ok &&
              sg.shifted &&
              sg.bothParties &&
              sg.confidence !== 'low'
            ) {
              await laughterDeposit(
                buildJointReframeEngram({
                  conversationId,
                  turnId,
                  userMessage: userText,
                  normalisedText: detect.normalisedText,
                  cues: detect.cues,
                  selfFiredBiteIndices,
                  firedAt,
                  detector: detect.detector,
                  semantic: detect.semantic,
                  sharedGround: {
                    before: sg.before,
                    after: sg.after,
                    confidence: sg.confidence,
                  },
                }),
              );
            }
          }
        } else if (detect.matched && selfFiredBiteIndices.length > 0) {
          // Stage 201-S cheap path: same-turn co-occurrence join.
          await laughterDeposit(
            buildJointReframeEngram({
              conversationId,
              turnId,
              userMessage: userText,
              normalisedText: detect.normalisedText,
              cues: detect.cues,
              selfFiredBiteIndices,
              firedAt,
              detector: detect.detector,
              semantic: detect.semantic,
            }),
          );
        }
      } catch (err) {
        // Fail-silent on detect/deposit errors (mirrors Stage 200). Log
        // once per error for operator forensics.
        // eslint-disable-next-line no-console
        console.warn('[laughter:witness] subscriber failure:', err);
      }
    })();
  }, [
    enabled,
    conversationId,
    sharedGroundEnabled,
    turn.turnId,
    turn.turnEnded,
    turn.bites,
    turn.swatEntries,
    latestUserMsg?.id,
    latestUserMsg?.text,
  ]);
}
